import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '../services/api'

export interface User {
  id: number
  email: string
  name: string
  roles: string[]
}

export interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, otp: string) => Promise<boolean>
  requestOtp: (email: string) => Promise<boolean>
  logout: () => Promise<void>
  isAuthenticated: boolean
  isAdmin: boolean
  isStaff: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!user
  const isAdmin = user?.roles.includes('admin') ?? false
  const isStaff = user?.roles.some(role => ['admin', 'staff'].includes(role)) ?? false

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await api.getCurrentUser()
        setUser(userData)
      } catch (error) {
        // User is not authenticated
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  const requestOtp = async (email: string): Promise<boolean> => {
    try {
      await api.requestOtp(email)
      return true
    } catch (error) {
      console.error('OTP request failed:', error)
      return false
    }
  }

  const login = async (email: string, otp: string): Promise<boolean> => {
    try {
      const userData = await api.verifyOtp(email, otp)
      setUser(userData)
      return true
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await api.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setUser(null)
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    requestOtp,
    logout,
    isAuthenticated,
    isAdmin,
    isStaff,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
